import { findLesson } from "./_helpers";
import { LessonShell } from "@/components/LessonShell";
import { AnimatedExplainer } from "@/components/AnimatedExplainer";
import { Chapter, Prose, TheoryBox, MTQuote, Glossary, Term, Steps, Step } from "@/components/mt/MTBox";
import { NoteImage } from "@/components/mt/NoteImage";
import { IntervalExplorer } from "@/components/mt/IntervalExplorer";
import { CircleOfFifths } from "@/components/CircleOfFifths";
import { NextUp } from "@/components/Callouts";

export default function MusicTheory7() {
  const { lesson, course, accent } = findLesson("music-theory", "7");
  return (
    <LessonShell
      course={course}
      courseSlug="music-theory"
      lessonOrder={lesson.order}
      title={lesson.title}
      description={lesson.desc}
      videoUrl={lesson.video}
      thumbUrl={lesson.thumb}
      accent={accent}
    >
      <AnimatedExplainer
        src="/animations/music-theory/MT7-Intervals-Animated.mp4"
        caption="12 intervals — distance, name & vibe (resolution/anticipation/mystery/tension)"
        hint="≈ 46 sec · watch first"
      />
      <Chapter number="07" eyebrow="Music Theory · Chapter Seven" title="How to actually remember intervals">
        <p>
          You've already met intervals as the building blocks of scales and chords. The next
          challenge is to <em>recall</em> them on demand — to know, instantly, that the major
          third up from E♭ is G, or that C's perfect fifth is G, without counting semitones in
          your head every time. This chapter is a workshop full of tricks for making those
          recalls automatic.
        </p>
        <MTQuote>
          It can get a bit daunting. So let's get some help in this chapter, or in this
          lecture. The first thing I'd like you to do is write down all your notes in the
          circle of fifths.
        </MTQuote>
      </Chapter>

      <Chapter eyebrow="The first tool" title="Write the circle of fifths from memory">
        <Prose>
          <p>
            The single most useful intervals in tonal music are the <strong>perfect 5th</strong>
            and the <strong>perfect 4th</strong>. The circle of fifths gives you both at once,
            for free, on every root: read clockwise for fifths, counter-clockwise for fourths.
            Memorising the circle removes a huge chunk of mental arithmetic from the rest of your
            theory life.
          </p>
        </Prose>

        <div className="mx-auto flex max-w-3xl justify-center py-4">
          <CircleOfFifths size={360} />
        </div>

        <Steps>
          <Step>
            <strong>Clockwise from C:</strong> C → G → D → A → E → B → F♯ → C♯ (D♭) → A♭ → E♭ → B♭ → F → C.
          </Step>
          <Step>
            <strong>Counter-clockwise from C:</strong> C → F → B♭ → E♭ → A♭ → D♭ → G♭ (F♯) → B → E → A → D → G → C.
          </Step>
          <Step>
            <strong>Practice both directions.</strong> Most people memorise the clockwise version
            and stop. Don't. Counter-clockwise gives you 4ths instantly — they're just as useful.
          </Step>
        </Steps>

        <Prose>
          <p>
            The circle is also a key-signatures cheat-sheet, a chord-progression generator, and
            a modulation map. We'll use it constantly in later chapters — so the more deeply
            it's burned into your memory now, the better.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The alphabet drill" title="Counting musical letters fluently">
        <Prose>
          <p>
            Western music uses only seven letters: A B C D E F G. Then it cycles. To find any
            interval by quantity (3rd, 5th, 7th…), you need to count those seven letters
            <strong> starting on any of them</strong>. Most musicians are surprisingly slow at
            this. The fix is a silly-sounding but effective drill.
          </p>
        </Prose>

        <TheoryBox label="The alphabet drill">
          <div className="text-center font-mt text-lg text-amber-200">
            <strong>Set a pulse · count the letters · cycle through every starting point</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (start on A, then B, then C, … reach G, then come back the other way)
          </div>
        </TheoryBox>

        <Steps>
          <Step><strong>Forwards from A:</strong> A B C D E F G A B C D E F G…</Step>
          <Step><strong>Forwards from B:</strong> B C D E F G A B C D E F G A…</Step>
          <Step><strong>Continue from C, D, E, F, G.</strong> The cycling gets harder as you change starting points; that's the work.</Step>
          <Step><strong>Backwards from A:</strong> A G F E D C B A G F E D C B…</Step>
          <Step><strong>Continue backwards from each letter.</strong> Same loop, opposite direction.</Step>
        </Steps>

        <Prose>
          <p>
            Once the alphabet is fluent in both directions, finding the 5th of A becomes
            instantaneous: A B C D <strong>E</strong>. Five letters, last one is your answer. The
            alphabet is the skeleton of every interval calculation; without it fluent, every
            interval is a stumble.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="The shortcuts" title="Formulas for the rest of the intervals">
        <Prose>
          <p>
            The circle gives you 4ths and 5ths. The alphabet drill gives you fluent letter
            counting. For the remaining intervals, a small handful of formulas does most of the
            work — the rest you simply commit to memory.
          </p>
        </Prose>

        <Glossary>
          <Term name="Major 3rd · 4 semitones" def="Just memorise the twelve. They're too important to compute every time." />
          <Term name="Minor 3rd · 3 semitones" def="One semitone below the major 3rd. Same advice — memorise." />
          <Term name="Major 6th · perfect 5th + 2" def="Find the 5th, then go up two more semitones. C → G → A." />
          <Term name="Minor 6th · perfect 5th + 1" def="Same idea, only one semitone above the 5th. C → G → A♭." />
          <Term name="Major 7th · octave − 1" def="One semitone below the octave. C → B." />
          <Term name="Minor 7th · octave − 2" def="Two semitones below the octave. C → B♭." />
          <Term name="Major 2nd · root + 2" def="A whole step. Trivial — but worth saying out loud." />
          <Term name="Minor 2nd · root + 1" def="A half step. Easiest interval to find; sometimes mistaken for harder." />
          <Term name="Tritone · P4 + 1 or P5 − 1" def="Sits exactly between the 4th and the 5th. Augmented 4th = diminished 5th." />
        </Glossary>

        <TheoryBox label="The major-scale-as-cheat-sheet trick">
          If you already know your major scales, you already know seven intervals on each root:
          <strong> M2, M3, P4, P5, M6, M7, P8</strong>. They are nothing but the seven notes of
          the major scale, sung in order. The minors and the tritone are the corrections you make
          to that base reading.
        </TheoryBox>
      </Chapter>

      <Chapter eyebrow="Hear them" title="Click every interval and let the ear catch up">
        <Prose>
          <p>
            Theory drills the brain; the explorer below drills the ear. Pick a root and click
            through every interval. After a few rounds, the formulas start to be confirmed by
            sensation, not just by calculation — you feel the leap of a fifth versus the lean of
            a fourth, the brightness of a major third versus the shadow of a minor third.
          </p>
        </Prose>

        <IntervalExplorer root="C" />
      </Chapter>

      <Chapter eyebrow="The 'rule of nine'" title="A clever inversion shortcut">
        <Prose>
          <p>
            If you ever blank on an interval, you can flip the question and use the
            <strong> rule of nine</strong>. Whatever interval you're stuck on, subtract its
            quantity from 9 and reverse the direction. The number you land on is the inverse
            interval — and computing that inverse is often easier.
          </p>
        </Prose>

        <TheoryBox label="Inversion rule">
          <div className="text-center font-mt text-xl text-amber-200">
            <strong>Original interval + Inversion = 9</strong>
          </div>
          <div className="mt-2 text-center text-[13px] text-nsm-dim">
            (3rd ↔ 6th · 4th ↔ 5th · 2nd ↔ 7th · major ↔ minor · perfect stays perfect)
          </div>
        </TheoryBox>

        <Prose>
          <p>
            Example: stuck on the minor 6th of E? Don't compute it forwards. Ask instead: <em>E
            is the major 3rd of what?</em> The answer is C. So C is E's minor 6th by inversion.
            Major becomes minor on inversion; diminished becomes augmented; perfect stays
            perfect. One trick, two intervals for the price of one.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="A note on extensions" title="9, 11, 13 belong with 7th chords">
        <Prose>
          <p>
            You'll occasionally see intervals labelled <strong>9</strong>, <strong>11</strong>,
            and <strong>13</strong>. These are simply the 2, 4, and 6 raised by an octave — and
            they only really come into play once you're stacking on top of a 7th chord. So the
            ♭9, ♯11, and 13 you see in jazz lead sheets are the same letters as 2, 4, 6, just
            higher up the stack. Don't worry about them yet; the basic twelve intervals come
            first.
          </p>
          <p>
            Similarly, you'll find <em>diminished 7ths</em> in older sheet music — these are
            the same pitch as a major 6th, but spelled differently to satisfy the rules of
            classical notation (B-double-flat instead of A, for example). The note sounds
            identical; the spelling tells you where it came from.
          </p>
        </Prose>
      </Chapter>

      <Chapter eyebrow="Homework" title="One root a day, every interval">
        <Steps>
          <Step>
            <strong>Pick a root.</strong> A different one each day. Hit every key over a week.
          </Step>
          <Step>
            <strong>Write all thirteen from that root.</strong> Unison, m2, M2, m3, M3, P4,
            tritone, P5, m6, M6, m7, M7, octave — twelve steps, thirteen names.
          </Step>
          <Step>
            <strong>Play each one and sing it.</strong> Both ascending and descending. The voice
            cements what the eye writes.
          </Step>
          <Step>
            <strong>Use the rule of nine to check yourself.</strong> Each interval and its
            inversion should sum to 9 in quantity and swap quality.
          </Step>
        </Steps>
        <NoteImage
          src="/notes/music-theory/finals/07a-intervals-of-A-and-vibes.png"
          caption="Jason's full interval table for A major — every interval (unison, M2, M3, P4, P5, M6, M7, octave plus the chromatic ones m2/m3/♭5/m6/m7) labelled with its mood: Resolution / Anticipation / Mystery / Tension. This 4-column 'vibe' chart is the foundation of how the ear hears every chord and melody."
        />
      </Chapter>

      <NextUp
        to="/lesson/music-theory-8"
        label="Chapter 8 · Theory of Intervals (Part 2)"
        why="Now that you can find any interval, the next chapter classifies them by emotion — resolution, tension, anticipation, mystery — so you can use them like a composer."
      />
    </LessonShell>
  );
}
